import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { usePagination } from "@/lib/hooks/use-pagination";
import { useCostTypes, useProducts } from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { TableCardSkeleton } from "../../skeleton-loaders";
import { useProductsTabContext } from "../products-tab-context";
import { T } from "./page.i18n";

export default function ProductsCostPage() {
  const {
    collectionId,
    debouncedSearch,
    openEdit,
    openDelete,
    loadingProductId,
    checkingDeleteId,
  } = useProductsTabContext();
  const { products, isLoading: productsLoading } = useProducts(collectionId, debouncedSearch);
  const { costTypes, isLoading: costTypesLoading } = useCostTypes(collectionId);
  const t = useT(T);
  const localize = useLocalize();
  const {
    page,
    setPage,
    pageSize,
    totalCount,
    totalPages,
    pageItems: paginatedProducts,
  } = usePagination(products, 10);

  if ((productsLoading && products.length === 0 && !debouncedSearch) || costTypesLoading) {
    return <TableCardSkeleton columns={Math.max(2 + costTypes.length, 3)} />;
  }

  if (products.length === 0 && debouncedSearch.trim() !== "") {
    return <EmptyState>{t.noProductsMatch}</EmptyState>;
  }

  if (products.length === 0) {
    return <EmptyState>{t.noProducts}</EmptyState>;
  }

  return (
    <>
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.productName}</TableHead>
            {costTypes.map((ct) => (
              <TableHead key={ct.id}>{localize(ct.nameI18n)}</TableHead>
            ))}
            <TableActionsHead>{t.actions}</TableActionsHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedProducts.map((product) => (
            <TableRow key={product.id}>
              <TableCell className="font-medium">{localize(product.nameI18n)}</TableCell>
              {costTypes.map((ct) => {
                const cv = product.costValues.find((c) => c.costTypeId === ct.id);
                const effective = cv ? (cv.overriddenValue ?? cv.calculatedValue) : null;
                return (
                  <TableCell key={ct.id}>
                    {effective !== null ? (
                      `${effective.toFixed(2)}%`
                    ) : (
                      <span className="text-muted-foreground italic text-xs">{t.notSet}</span>
                    )}
                  </TableCell>
                );
              })}
              <TableActionsCell>
                <Button
                  variant="ghost"
                  size="icon"
                  tooltip={t.edit}
                  onClick={() => openEdit(product.id)}
                  loading={loadingProductId === product.id}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  tooltip={t.delete}
                  loading={checkingDeleteId === product.id}
                  onClick={() => openDelete({ id: product.id, nameI18n: product.nameI18n })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableActionsCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </>
  );
}
